import { AuthSession, AuthUserRow, Role } from './types';
import { saveSession, loadSession, clearSession } from './tokenStorage';
import { apiFetch, buildAuthHeaders, parseApiError } from './apiFetch';

const BASE = '';

export async function login(username: string, password: string): Promise<AuthSession> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
  const data = await res.json();
  const session: AuthSession = {
    token: data.token,
    username: data.username,
    role: data.role,
    mustChangePassword: data.mustChangePassword,
  };
  saveSession(session);
  return session;
}

export async function changePassword(
  oldPassword: string | null,
  newPassword: string
): Promise<AuthSession> {
  const res = await apiFetch(`${BASE}/auth/change-password`, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({ oldPassword, newPassword }),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
  const data = await res.json();
  const session = loadSession()!;
  const updated: AuthSession = { ...session, token: data.token, mustChangePassword: false };
  saveSession(updated);
  return updated;
}

export async function listUsers(): Promise<AuthUserRow[]> {
  const res = await apiFetch(`${BASE}/auth/users`, { headers: buildAuthHeaders() });
  if (!res.ok) throw new Error(await parseApiError(res));
  const data = await res.json();
  return data.users;
}

export async function createUser(
  username: string,
  password: string,
  role: Role
): Promise<void> {
  const res = await apiFetch(`${BASE}/auth/users`, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({ username, password, role }),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
}

export async function deleteUser(username: string): Promise<void> {
  const res = await apiFetch(`${BASE}/auth/users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
}

export interface AuditLine {
  time?: string;
  user?: string;
  ip?: string;
  action?: string;
  detail?: string;
  raw?: string;
}

export async function fetchAuditLog(lines = 200): Promise<AuditLine[]> {
  const res = await apiFetch(`${BASE}/audit?lines=${lines}`, { headers: buildAuthHeaders() });
  if (!res.ok) throw new Error(await parseApiError(res));
  const data = await res.json();
  return data.lines ?? [];
}

export async function logout(): Promise<void> {
  const session = loadSession();
  if (session?.token) {
    try {
      await fetch(`${BASE}/auth/logout`, {
        method: 'POST',
        headers: buildAuthHeaders(),
      });
    } catch {
      // clear local session even if server call fails
    }
  }
  clearSession();
}
